import { Children, isValidElement, ReactNode } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { documentationMarkdownUrlTransform } from '../../utils/documentationInlineAttachments';
import { DocumentationMarkdownImage } from './DocumentationMarkdownImage';
import { DocumentationMermaidDiagram } from './DocumentationMermaidDiagram';

type DocumentationMarkdownContentProps = {
  value?: string | null;
  className?: string;
  emptyLabel?: string;
  /** Necessário para resolver imagens docs-attachment:{id}. */
  pageId?: string | null;
};

function extractCodeText(children: ReactNode): string {
  if (children == null || children === false || children === true) {
    return '';
  }
  if (typeof children === 'string' || typeof children === 'number') {
    return String(children).replace(/\n$/, '').trimEnd();
  }
  if (Array.isArray(children)) {
    return children.map(extractCodeText).join('').replace(/\n$/, '').trimEnd();
  }
  if (isValidElement<{ children?: ReactNode }>(children)) {
    return extractCodeText(children.props.children);
  }
  return '';
}

function getCodeLanguage(className?: string): string | null {
  const match = /language-([a-z0-9_+-]+)/i.exec(className ?? '');
  return match?.[1]?.toLowerCase() ?? null;
}

function isMermaidNode(node: ReactNode): boolean {
  return isValidElement(node) && node.type === DocumentationMermaidDiagram;
}

/**
 * Render Markdown para wiki de documentação (GFM + Mermaid).
 */
export function DocumentationMarkdownContent({
  value,
  className = '',
  emptyLabel = 'Sem conteúdo.',
  pageId,
}: DocumentationMarkdownContentProps) {
  const markdown = value?.trim() ?? '';
  if (!markdown) {
    return <p className={`text-sm text-secondary-500 ${className}`.trim()}>{emptyLabel}</p>;
  }

  return (
    <div className={`docs-md-prose ${className}`.trim()}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        urlTransform={documentationMarkdownUrlTransform}
        components={{
          h1: ({ children }) => <h2 className="docs-md-prose__h1">{children}</h2>,
          h2: ({ children }) => <h2 className="docs-md-prose__h2">{children}</h2>,
          h3: ({ children }) => <h3 className="docs-md-prose__h3">{children}</h3>,
          h4: ({ children }) => <h4 className="docs-md-prose__h4">{children}</h4>,
          p: ({ children }) => <p>{children}</p>,
          ul: ({ children }) => <ul>{children}</ul>,
          ol: ({ children }) => <ol>{children}</ol>,
          li: ({ children }) => <li>{children}</li>,
          strong: ({ children }) => <strong>{children}</strong>,
          em: ({ children }) => <em>{children}</em>,
          blockquote: ({ children }) => <blockquote>{children}</blockquote>,
          hr: () => <hr />,
          a: ({ href, children }) => (
            <a href={href} target="_blank" rel="noreferrer">
              {children}
            </a>
          ),
          img: ({ src, alt }) => (
            <DocumentationMarkdownImage src={src} alt={alt} pageId={pageId} />
          ),
          code: ({ className: codeClassName, children }) => {
            const language = getCodeLanguage(codeClassName);
            if (language === 'mermaid') {
              return <DocumentationMermaidDiagram chart={extractCodeText(children)} />;
            }
            if (language) {
              return <code className={codeClassName}>{children}</code>;
            }
            return <code className="docs-md-prose__inline-code">{children}</code>;
          },
          pre: ({ children }) => {
            const nodes = Children.toArray(children);
            if (nodes.length === 1 && isMermaidNode(nodes[0])) {
              return <>{nodes[0]}</>;
            }

            const child = nodes.find((node) => isValidElement(node));
            if (isValidElement<{ className?: string; children?: ReactNode }>(child)) {
              const language = getCodeLanguage(child.props.className);
              if (language === 'mermaid') {
                return <DocumentationMermaidDiagram chart={extractCodeText(child.props.children)} />;
              }
            }

            return <pre className="docs-md-prose__pre">{children}</pre>;
          },
          table: ({ children }) => (
            <div className="docs-md-prose__table-wrap">
              <table>{children}</table>
            </div>
          ),
          thead: ({ children }) => <thead>{children}</thead>,
          tbody: ({ children }) => <tbody>{children}</tbody>,
          tr: ({ children }) => <tr>{children}</tr>,
          th: ({ children }) => <th>{children}</th>,
          td: ({ children }) => <td>{children}</td>,
        }}
      >
        {markdown}
      </ReactMarkdown>
    </div>
  );
}
